import createSubscription from 'create-subscription';
import React, { Component, ReactNode } from 'react';
import { identity, merge, Observable, OperatorFunction } from 'rxjs';
import { map } from 'rxjs/operators';
import { shallowEqualObjects } from 'shallow-equal';
import { getGlobalWindowOrThrow } from '../helpers/dom/global';
import { AnimationFrameSchedulerDelegate } from '../helpers/rxjs/animationFrameScheduler';
import { auditInAnimationFrame } from '../helpers/rxjs/auditInAnimationFrame';
import { isService, Service } from '../services/base/service';

type ServiceOrServices = Service | { [key: string]: Service };

export interface ServiceSubscriptionProps<S extends ServiceOrServices> {
  source: S;
  filter?: OperatorFunction<any, any>;
  /** @deprecated */
  shouldSkipDebounce?: boolean;
  /** Used to schedule updates. If not provided, warns and falls back to the `window` global. */
  window?: AnimationFrameSchedulerDelegate;
  children: (service: S) => ReactNode;
}

interface ServiceSubscriptionState<S extends ServiceOrServices> {
  source: SubscriptionSource<S>;
}

interface SubscriptionSource<S extends ServiceOrServices> {
  serviceOrServices: S;
  filter: OperatorFunction<any, any>;
  window: AnimationFrameSchedulerDelegate;
}

/**
 * Declarative way to subscribe to a service's state changes.
 *
 * Safe for use with React async rendering.
 */
export class ServiceSubscription<S extends ServiceOrServices> extends Component<
  ServiceSubscriptionProps<S>,
  ServiceSubscriptionState<S>
> {
  // We must declare this here to be able to use the type variable S. This subscription maps to NaN so that
  // adjacent values are never equal and the subscription always renders when changes$ emits.
  private readonly Subscription = createSubscription.createSubscription<SubscriptionSource<S>, number>({
    getCurrentValue: () => NaN,
    subscribe: (source, callback) => {
      const observable = getChanges$(source.serviceOrServices).pipe(
        source.filter,
        auditInAnimationFrame(source.window),
        map(() => NaN),
      );
      const subscription = observable.subscribe(callback);
      return () => subscription.unsubscribe();
    },
  });

  constructor(props: ServiceSubscriptionProps<S>) {
    super(props);

    this.state = {
      source: getSource(props),
    };
  }

  static getDerivedStateFromProps<S extends ServiceOrServices>(
    nextProps: ServiceSubscriptionProps<S>,
    prevState: ServiceSubscriptionState<S>,
  ): Partial<ServiceSubscriptionState<S>> | null {
    const nextSource = getSource(nextProps);

    if (!isSourceEqual(prevState.source, nextSource)) {
      return { source: nextSource };
    }

    return null;
  }

  private readonly maybeRenderChildren = () => {
    const { source, children } = this.props;

    // Catch this case to make sure we return null rather than undefined, otherwise
    // we end up with "Nothing was returned from render" errors. This happens because undefined
    // is a valid ReactNode but not a valid return type from our a class component render function,
    // and the render function this is used just returns the children.
    return children(source) || null;
  };

  render() {
    const { Subscription } = this;
    const { source } = this.state;

    return <Subscription source={source}>{this.maybeRenderChildren}</Subscription>;
  }
}

function getChanges$(serviceOrServices: ServiceOrServices): Observable<void> {
  if (isService(serviceOrServices)) {
    const service = serviceOrServices;
    return service.changes$;
  }

  const services = serviceOrServices;
  return merge(...Object.values(services).map(service => service.changes$));
}

function getSource<S extends ServiceOrServices>(props: ServiceSubscriptionProps<S>): SubscriptionSource<S> {
  if (props.shouldSkipDebounce) {
    // eslint-disable-next-line no-console
    console.warn('The ServiceSubscription#shouldSkipDebounce prop is deprecated');
  }

  if (!props.window) {
    // eslint-disable-next-line no-console
    console.warn('The ServiceSubscription#window prop is missing, falling back to global window');
  }

  return {
    serviceOrServices: props.source,
    filter: props.filter || identity,
    window: props.window || getGlobalWindowOrThrow(),
  };
}

function isSourceEqual(a: SubscriptionSource<any>, b: SubscriptionSource<any>): boolean {
  if (isService(a.serviceOrServices)) {
    if (isService(b.serviceOrServices)) {
      return shallowEqualObjects(a, b);
    }

    return false;
  }

  if (isService(b.serviceOrServices)) {
    return false;
  }

  return (
    shallowEqualObjects(a.serviceOrServices, b.serviceOrServices) &&
    a.filter === b.filter &&
    a.window === b.window
  );
}
